import { ApplicationController, useDebounce } from 'stimulus-use'

export default class extends ApplicationController {
  static debounces = ['updateSuggestions']

  static targets = [
    "input",
    "suggestionDropdown",
    "placeServicesNode",
    "suggestedAddressTemplate",
    "suggestedEstablishmentTemplate",
    "defaultSuggestionsTemplate",
    "persistedItemTemplate"
  ]

  static values = {
    autocompleteCountries: Array,
    acceptedTypes: Array,
    frontFilteredTypes: Array,
    dropdownTitle: String
  }

  // This component controller works as an outlet for another controller.
  // The other controller must have a method called `selectGooglePlace` that receives a placeData object.
  // This method must be attributed to the outlet in the other controller.
  //
  // formGoogleAutocompleteInputOutletConnected(outlet, element) {
  //   outlet.selectGooglePlace = this.selectGooglePlace.bind(this)
  // }
  //
  // selectGooglePlace(placeData) {
  //   do things
  // }

  connect() {
    useDebounce(this, { wait: 200 })
    this.autocompleteService = new google.maps.places.AutocompleteService();
    this.createPortal()
  }

  // Create a portal for the suggestion dropdown to avoid overflow issues
  createPortal() {
    this.dropdown = document.body.appendChild(this.suggestionDropdownTarget)
    this.dropdown.style.position = 'absolute'
    this.dropdown.style.zIndex = '9999'
  }

  disconnect() {
    if (this.dropdown) this.dropdown.remove()
    this.stopTrackingScrollAndResize()
  }

  updateSuggestions() {
    this.openSuggestionDropdown()
    this.startTrackingScrollAndResize()

    if (this.inputTarget.value.length > 2) {
      const params = {
        input: this.inputTarget.value,
        types: this.acceptedTypesValue,
        componentRestrictions: { country: this.autocompleteCountriesValue }
      }
      this.autocompleteService.getPlacePredictions(params, this.fetchGooglePlaces.bind(this))
    } else {
      this.appendDefaultSuggestions()
    }

    if (this.dropdown.children.length === 0) {
      this.closeSuggestionDropdown()
    }
  }

  async fetchGooglePlaces(predictions) {
    this.dropdown.innerHTML = ""

    if (predictions != null && predictions.length > 0) {
      this.placesService = new google.maps.places.PlacesService(this.placeServicesNodeTarget);

      predictions.forEach(placePrediction => {
        const isEstablishment = placePrediction.types?.includes("establishment")
        const template = isEstablishment ? this.suggestedEstablishmentTemplateTarget : this.suggestedAddressTemplateTarget
        const item = template.content.firstElementChild.cloneNode(true);
        const isAcceptedType = this.frontFilteredTypesValue.length === 0 || placePrediction.types.some(type => this.frontFilteredTypesValue.includes(type));

        if (!isAcceptedType) return;

        if (isEstablishment) {
          item.children[1].firstElementChild.textContent = placePrediction.structured_formatting.main_text;
          item.children[1].lastElementChild.textContent = placePrediction.structured_formatting.secondary_text;
        } else {
          item.children[1].firstElementChild.textContent = placePrediction.description;
        }

        item.addEventListener("click", () => {
          this.placesService.getDetails({ placeId: placePrediction.place_id }, (placeData) => {
            this.selectGooglePlace(placeData)
            this.dropdown.classList.add("d-none");
          })
        })

        this.dropdown.append(item);
      })
    } else {
      this.appendDefaultSuggestions()
    }

    this.appendPersistedItem()
  }

  appendPersistedItem() {
    if (!this.hasPersistedItemTemplateTarget) return

    const item = this.persistedItemTemplateTarget.content.firstElementChild.cloneNode(true)

    item.addEventListener("click", () => { this.selectPersistedItem(item) })

    this.dropdown.append(item)
  }

  selectPersistedItem() {
    // Implement this method in the other controller
  }

  appendDefaultSuggestions() {
    if (Array.from(this.defaultSuggestionsTemplateTarget.content.children).length === 0) return

    this.dropdown.innerHTML = ""

    const titleItem = document.createElement("div")
    titleItem.classList.add("p-2", "font-weight-bolder")
    titleItem.textContent = this.dropdownTitleValue
    this.dropdown.append(titleItem)

    Array.from(this.defaultSuggestionsTemplateTarget.content.children).forEach(child => {
      const item = child.cloneNode(true)
      const formattedAddress = item.children[1].firstElementChild.textContent

      item.addEventListener("click", () => {
        this.inputTarget.value = formattedAddress
        this.inputTarget.setAttribute("value", formattedAddress)

        this.dropdown.classList.add("d-none");
        this.selectDefaultSuggestion(formattedAddress)
        this.inputTarget.blur()
      })

      this.dropdown.append(item)
    })
  }

  selectDefaultSuggestion() {
    // Implement this method in the other controller
  }

  focusNextSuggestion() {
    const selectedSuggestionIndex = Array.from(this.dropdown.children).indexOf(this.selectedSuggestion) || 0
    const nextSuggestionIndex = selectedSuggestionIndex + 1 >= this.dropdown.children.length ? 0 : selectedSuggestionIndex + 1
    const nextSuggestion = this.dropdown.children[nextSuggestionIndex]

    this.selectedSuggestion?.classList?.remove("selected")

    nextSuggestion.classList.add("selected")
    this.selectedSuggestion = nextSuggestion
  }

  focusPreviousSuggestion() {
    const selectedSuggestionIndex = Array.from(this.dropdown.children).indexOf(this.selectedSuggestion) || 0
    const previousSuggestionIndex = selectedSuggestionIndex - 1 < 0 ? this.dropdown.children.length - 1 : selectedSuggestionIndex - 1
    const previousSuggestion = this.dropdown.children[previousSuggestionIndex]

    this.selectedSuggestion?.classList?.remove("selected")

    previousSuggestion.classList.add("selected")
    this.selectedSuggestion = previousSuggestion
  }

  selectSuggestion() {
    this.selectedSuggestion?.click()
  }

  openSuggestionDropdown() {
    this.repositionDropdown()
    this.dropdown.classList.remove("d-none")
  }

  closeSuggestionDropdown(event) {
    if (event instanceof PointerEvent) {
      if (this.dropdown.contains(event?.target)) return
      if (this.inputTarget.contains(event?.target)) return
    }

    this.dropdown.classList.add("d-none");
  }

  repositionDropdown() {
    const rect = this.inputTarget.getBoundingClientRect()
    const menuHeight = this.dropdown.offsetHeight
    const spaceBelow = window.innerHeight - rect.bottom
    const margin = 10

    // If there's not enough space below, position above
    if (spaceBelow < menuHeight) {
      this.dropdown.style.top = `${rect.top + window.scrollY - menuHeight - margin}px`
    } else {
      this.dropdown.style.top = `${rect.bottom + window.scrollY + margin}px`
    }

    this.dropdown.style.left = `${rect.left + window.scrollX}px`
    this.dropdown.style.width = `${rect.width}px`
  }

  startTrackingScrollAndResize() {
    this.repositionDropdownHandler = this.repositionDropdown.bind(this)
    window.addEventListener("scroll", this.repositionDropdownHandler, true) // use capture to catch scrolls on all elements
    window.addEventListener("resize", this.repositionDropdownHandler)
  }

  stopTrackingScrollAndResize() {
    window.removeEventListener("scroll", this.repositionDropdownHandler, true)
    window.removeEventListener("resize", this.repositionDropdownHandler)
  }
}
